var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import Button from "@/components/ui/button/Button.vue";
import ContextMenu from "@/components/ui/context-menu/ContextMenu.vue";
import Panel from "@/components/ui/panel/Panel.vue";
import Select from "@/components/ui/select/Select.vue";
import { ChevronDownIcon, FilterIcon } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import { fetchKpiCompliance, fetchWorkingDaysProgress, KPI_COMPLIANCE_TYPES, } from "../../api";
import { getCategoryColor } from "../../utils";
import { DEFAULT_KPI_COMPLIANCE_TYPE } from "../../config";
import Donut from "@/components/common/donut/Donut.vue";
import BarsChart from "@/components/common/bars-chart/BarsChart.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'KpiCompliancePanel',
    props: {
        currentPeriodLabel: { type: String, required: false }
    },
    setup(__props, { expose: __expose }) {
        __expose();
        // State
        const isLoading = ref(true);
        const error = ref(null);
        const complianceData = ref(null);
        const workingDaysProgress = ref(null);
        const selectedType = ref(DEFAULT_KPI_COMPLIANCE_TYPE);
        const selectedDistributors = ref([]);
        const distributorSourceData = ref([]);
        // Donut Chart Data (for total compliance)
        const donutChartData = computed(() => {
            if (!complianceData.value || Array.isArray(complianceData.value)) {
                return { labels: [], datasets: [] };
            }
            const totalData = complianceData.value;
            const compliant = totalData.total;
            const nonCompliant = 100 - totalData.total;
            return {
                labels: ["Compliant", "Non-Compliant"],
                datasets: [
                    {
                        data: [compliant, nonCompliant],
                        backgroundColor: [getCategoryColor(4), "#E0E0E0"], // Yellow color for compliant
                        borderWidth: 0,
                    },
                ],
            };
        });
        // Custom plugin for center text
        const centerTextPlugin = {
            id: "centerText",
            beforeDraw: (chart) => {
                const { width, height, ctx } = chart;
                const totalData = complianceData.value;
                const compliantPercent = (totalData === null || totalData === void 0 ? void 0 : totalData.total) || 0;
                ctx.restore();
                // Calculate font size based on chart size (responsive)
                const fontSize = Math.floor(height / 6);
                ctx.font = `bold ${fontSize}px Arial, sans-serif`;
                ctx.textBaseline = "middle";
                ctx.fillStyle = "#535353"; // Normal text color
                const text = `${compliantPercent.toFixed(1)}%`;
                const textX = Math.round((width - ctx.measureText(text).width) / 2);
                const textY = height / 2;
                ctx.fillText(text, textX, textY);
                ctx.save();
            },
        };
        const donutChartOptions = computed(() => ({
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: {
                    display: false, // Hide legend
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            return `${context.label}: ${context.parsed.toFixed(1)}%`;
                        },
                    },
                },
                datalabels: {
                    display: false, // Remove segment labels
                },
                centerText: true, // Enable our custom plugin
            },
            cutout: "60%", // Balanced donut ring with more colored area
        }));
        // Bar Chart Data (for distributor compliance)
        const barChartData = computed(() => {
            if (!complianceData.value || !Array.isArray(complianceData.value)) {
                return { labels: [], datasets: [] };
            }
            const distribuidorData = complianceData.value;
            const labels = distribuidorData.map((item) => item.distribuidor || "(Sin nombre)");
            const values = distribuidorData.map((item) => item.total);
            return {
                labels,
                datasets: [
                    {
                        label: "Compliance",
                        data: values,
                        backgroundColor: getCategoryColor(0),
                        borderWidth: 1,
                    },
                ],
            };
        });
        const barChartHeight = computed(() => {
            if (!complianceData.value || !Array.isArray(complianceData.value)) {
                return 400;
            }
            const distribuidorData = complianceData.value;
            const itemCount = distribuidorData.length;
            // Calculate dynamic height: minimum 400px, add 60px per item
            return Math.max(400, itemCount * 20);
        });
        const barChartOptions = computed(() => ({
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y", // Horizontal bars
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            return `${context.dataset.label}: ${context.parsed.x.toFixed(1)}%`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: true },
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                        callback: (value) => `${value}%`,
                    },
                },
                y: {
                    grid: { display: false },
                },
            },
        }));
        // Computed
        const showDistributorSelect = computed(() => selectedType.value === "distribuidor");
        const distributorOptions = computed(() => {
            if (selectedType.value !== "distribuidor") {
                return [];
            }
            return distributorSourceData.value
                .map((item) => { var _a; return ((_a = item.distribuidor) === null || _a === void 0 ? void 0 : _a.trim()) || "(Sin nombre)"; })
                .filter((value, index, values) => value.length > 0 && values.indexOf(value) === index)
                .sort((left, right) => left.localeCompare(right, "es", { sensitivity: "base" }))
                .map((distributor) => ({
                label: distributor,
                value: distributor,
            }));
        });
        const typeDisplayText = computed(() => {
            const currentType = KPI_COMPLIANCE_TYPES.find((type) => type.value === selectedType.value);
            return currentType ? currentType.label : "Tipo";
        });
        const typeMenuOptions = computed(() => {
            return KPI_COMPLIANCE_TYPES.map((type) => ({
                label: type.label,
                value: type.value,
            }));
        });
        const isEmptyData = computed(() => {
            if (!complianceData.value)
                return true;
            if (Array.isArray(complianceData.value)) {
                return complianceData.value.length === 0;
            }
            return false;
        });
        // Methods
        const handleTypeSelect = (option) => __awaiter(this, void 0, void 0, function* () {
            selectedType.value = option.value;
            yield loadData();
        });
        const loadData = () => __awaiter(this, void 0, void 0, function* () {
            try {
                isLoading.value = true;
                error.value = null;
                const [workingDaysResponse, complianceResponse] = yield Promise.all([
                    fetchWorkingDaysProgress(),
                    fetchKpiCompliance({
                        filter: selectedType.value === "distribuidor" ? "distribuidor" : undefined,
                        distributors: selectedType.value === "distribuidor" ? selectedDistributors.value : undefined,
                    }),
                ]);
                workingDaysProgress.value = workingDaysResponse;
                complianceData.value = complianceResponse;
                if (selectedType.value === "distribuidor" && selectedDistributors.value.length === 0 && Array.isArray(complianceResponse)) {
                    distributorSourceData.value = complianceResponse;
                }
                if (selectedType.value !== "distribuidor") {
                    distributorSourceData.value = [];
                }
            }
            catch (err) {
                error.value = "Error al cargar los datos de KPI Compliance";
                console.error("Error loading KPI Compliance data:", err);
            }
            finally {
                isLoading.value = false;
            }
        });
        // Lifecycle
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            yield loadData();
        }));
        watch(showDistributorSelect, (isDistributorView) => {
            if (!isDistributorView && selectedDistributors.value.length > 0) {
                selectedDistributors.value = [];
            }
        });
        watch(selectedDistributors, () => __awaiter(this, void 0, void 0, function* () {
            if (!showDistributorSelect.value) {
                return;
            }
            yield loadData();
        }), { deep: true });
        watch(distributorOptions, (options) => {
            if (selectedDistributors.value.length === 0) {
                return;
            }
            const validValues = new Set(options.map((option) => option.value));
            selectedDistributors.value = selectedDistributors.value.filter((value) => validValues.has(value));
        });
        const __returned__ = { isLoading, error, complianceData, workingDaysProgress, selectedType, selectedDistributors, distributorSourceData, donutChartData, centerTextPlugin, donutChartOptions, barChartData, barChartHeight, barChartOptions, showDistributorSelect, distributorOptions, typeDisplayText, typeMenuOptions, isEmptyData, handleTypeSelect, loadData, Button, ContextMenu, Panel, Select, get ChevronDownIcon() { return ChevronDownIcon; }, get FilterIcon() { return FilterIcon; }, Donut, BarsChart };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
